import type { DatesSetArg, EventDropArg, MoreLinkArg } from "@fullcalendar/core"
import dayjs from "dayjs"
import React, { useEffect, useRef, useState } from "react"
import { type ConnectedProps, connect } from "react-redux"
import { useSearchParams } from "react-router-dom"

import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { type RootState } from "../../../store.ts"
import { DATE_FORMAT } from "../../../utils/date/date.ts"
import { DATE_FORMATS_OBJ } from "../../TeamManagementPage/constants/date-formats.ts"
import { calendarSetProp } from "../redux/calendar.slice.ts"
import {
  fetchEventDetails,
  fetchEvents,
  updateDraggableEvent
} from "../redux/calendar.thunks.ts"
import type {
  CalendarFilters,
  CalendarModalData,
  CalendarState
} from "../redux/calendar.types.ts"
import CalendarPageActions from "./CalendarPageActions.tsx"
import { Calendar } from "./components/Calendar.tsx"
import { rescheduleDroppedEvent } from "./helpers/reschedule-dropped-event.ts"

const CalendarPage = ({
  dateFormat,
  isMobile,
  events,
  filters,
  permissions,
  userInfo,
  setProp,
  fetchEvents,
  getEvent,
  updateDraggableEvent
}: ConnectedProps<typeof connector>) => {
  const prevDateRef = useRef<string | null>(null)
  const [searchParams] = useSearchParams()
  const focusDate = searchParams.get("date")
  const focusEventId = searchParams.get("event")
  const openedEventRef = useRef<string | null>(null)

  const timezone = userInfo?.timezone
  const userId = userInfo?.id
  const moduleAccess = permissions?.codes?.calendar?.calendar
  const moduleFeatures = permissions?.codes?.calendar?.features

  const dayJsDateFormat = dateFormat
    ? DATE_FORMATS_OBJ?.[dateFormat]?.label
    : null

  const handleSetProp = (
    key: keyof CalendarState,
    value: CalendarState[keyof CalendarState]
  ) => setProp({ key, value })

  const handleSetModalData = (data: CalendarModalData | null) =>
    handleSetProp("addModalData", data)

  const handleSetMoreEventsData = (data?: MoreLinkArg) => {
    if (data) {
      handleSetProp("moreEventsData", {
        date: data?.date,
        allSegs: data?.allSegs
      })
    } else {
      handleSetProp("moreEventsData", null)
    }
  }

  // Quick View's own loading/error state — deliberately not the shared
  // `fetching` flag, which also covers the whole-grid fetch. A failed fetch
  // previously left a click with no feedback at all: `fetchEventDetails.
  // rejected` only clears `fetching`, it never surfaces an error. This is
  // the one place an event is opened from (grid click, "+N more", and the
  // deep-link effect below all call it), so the loading/error state covers
  // every entry point rather than just one.
  const [openingEventId, setOpeningEventId] = useState<string | null>(null)
  const [failedEventId, setFailedEventId] = useState<string | null>(null)

  const handleSelectEvent = (id: string | null) => {
    if (!id) {
      handleSetProp("selectedEvent", id)
      return
    }

    setFailedEventId(null)
    setOpeningEventId(id)

    getEvent(id)
      .unwrap()
      .catch(() => setFailedEventId(id))
      .finally(() => setOpeningEventId(null))
  }

  const handleRetryEventDetails = () => {
    if (failedEventId) handleSelectEvent(failedEventId)
  }

  const handleDismissEventError = () => setFailedEventId(null)

  const handleFetchEvents = (filters: CalendarFilters) => {
    const { participants, ...restFilters } = filters

    fetchEvents({
      ...restFilters,
      page: 1,
      page_size: 100,
      participants: participants?.map((p) => p?.value)?.join(",") || null
    })
  }

  const handleFilterChange = (data: Partial<CalendarFilters>) => {
    handleSetProp("filters", { ...filters, ...data })
    handleFetchEvents({ ...filters, ...data })
  }

  const handleFetchEventsWithDates = async (dates: Partial<DatesSetArg>) => {
    // for some reason, the API token does not have time to install
    // if this is the first request, we wait for the token to install
    if (!prevDateRef.current) {
      await new Promise((resolve) => setTimeout(resolve, 0))
    }

    const newDate = dates.startStr

    if (prevDateRef.current === newDate) return
    if (newDate) prevDateRef.current = newDate

    handleFilterChange({
      start: dayjs(dates.start).add(-1, "day").format(DATE_FORMAT),
      end: dayjs(dates.end).add(1, "day").format(DATE_FORMAT)
    })
  }

  const handleDropEvent = (arg: EventDropArg) =>
    rescheduleDroppedEvent(arg, timezone, (data) =>
      updateDraggableEvent(data).unwrap()
    )

  // useEffect(() => {
  //   isFetching.current = fetching
  // }, [fetching])

  useEffect(() => {
    generateDocumentTitle("Calendar")
    handleSetProp("fetching", false)
  }, [])

  // A meeting opened from a Lead names itself in the URL. Guarded by a ref so
  // re-renders do not reopen a detail the user has already closed.
  useEffect(() => {
    if (!focusEventId || openedEventRef.current === focusEventId) return
    openedEventRef.current = focusEventId
    handleSelectEvent(focusEventId)
  }, [focusEventId])

  return (
    <>
      <CalendarPageActions
        dateFormat={dayJsDateFormat}
        userId={userId}
        timezone={timezone}
        permissions={{
          delete: !!permissions?.codes?.calendar?.calendar?.calendar_delete,
          edit: !!permissions?.codes?.calendar?.calendar?.calendar_edit,
          viewLead: !!permissions?.codes?.leads?.leads?.lead_view,
          viewProject: !!permissions?.codes?.projects?.projects?.project_view
        }}
        onSelectEvent={handleSelectEvent}
        openingEventId={openingEventId}
        failedEventId={failedEventId}
        onRetryEventDetails={handleRetryEventDetails}
        onDismissEventError={handleDismissEventError}
      />
      <Calendar
        timezone={timezone}
        focusDate={focusDate}
        filters={filters}
        isMobile={isMobile}
        events={events}
        userId={userId}
        //
        createAllowed={moduleAccess?.calendar_create}
        editAllowed={moduleAccess?.calendar_edit}
        viewAllAllowed={moduleFeatures?.calendar_view_all}
        //
        onSetModalData={handleSetModalData}
        onSelectEvent={handleSelectEvent}
        onSetMoreInfo={handleSetMoreEventsData}
        onFetchEvents={handleFetchEventsWithDates}
        onFilterChange={handleFilterChange}
        onDropEvent={handleDropEvent}
      />
    </>
  )
}

const mapStateToProps = (state: RootState) => ({
  dateFormat: state.auth.userInfo?.date_format,
  permissions: state.auth.permissions,
  isMobile: state.nav.isMobile,
  events: state.calendar?.events || [],
  filters: state.calendar?.filters,
  userInfo: state.auth.userInfo
})

const mapDispatchToProps = {
  fetchEvents: fetchEvents,
  getEvent: fetchEventDetails,
  setProp: calendarSetProp,
  updateDraggableEvent
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(CalendarPage)
